import { useEffect, useState, type JSX } from 'react';
import { Icon } from '../../components/Icon';
import { useProject, useUiContext } from '../../context';
import { chordFromEvent, findConflicts, formatChord, isReservedChord, resolveBindings, UNBOUND, type BindableAction } from '../../hotkeys/chords';
import { isMac, useHotkeyRegistry } from '../../hotkeys/HotkeyManager';
import { attempt } from '../../safe';

/** Waits for the next key chord; Escape cancels. Rendered in place of the "Add" button. */
function Capture({ onChord, onCancel }: { onChord(chord: string): void; onCancel(): void }): JSX.Element {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      if (event.code === 'Escape' && !event.ctrlKey && !event.altKey && !event.shiftKey && !event.metaKey) {
        onCancel();
        return;
      }
      const chord = chordFromEvent(event);
      if (chord) onChord(chord);
    };
    // Capture phase: the dialog's own Escape and Tab handling must not see these keys.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onChord, onCancel]);
  return (
    <span className="hotkey-capture" role="status">
      Press keys… (Esc cancels)
    </span>
  );
}

export function HotkeysTab(): JSX.Element {
  const { studio } = useUiContext();
  const registry = useHotkeyRegistry();
  const stored = useProject((p) => p.hotkeys);
  const [capturing, setCapturing] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const mac = isMac();

  const bindings = resolveBindings(registry.all as BindableAction[], stored);
  const conflicts = findConflicts(bindings);
  const labelOf = (id: string): string => registry.all.find((a) => a.id === id)?.label ?? id;
  const save = (id: string, chords: string[]): void =>
    void attempt(studio, () => studio.commands.setHotkey(id, chords.length === 0 ? [UNBOUND] : chords));

  const add = (id: string, chord: string): void => {
    setCapturing(null);
    if (isReservedChord(chord)) {
      setMessage(`${formatChord(chord, mac)} is reserved by the browser or the operating system and cannot be bound.`);
      return;
    }
    const current = bindings[id] ?? [];
    if (current.includes(chord)) return;
    const others = Object.entries(bindings).filter(([other, chords]) => other !== id && chords.includes(chord));
    setMessage(others.length > 0 ? `${formatChord(chord, mac)} is also bound to: ${others.map(([other]) => labelOf(other)).join(', ')}.` : null);
    save(id, [...current, chord]);
  };

  const groups = new Map<string, typeof registry.all>();
  for (const action of registry.all) {
    const group = action.group ?? 'Plugins';
    groups.set(group, [...(groups.get(group) ?? []), action]);
  }

  return (
    <div>
      <div className="ui-row" style={{ marginBottom: 8 }}>
        <span className="ui-grow ui-dim">Shortcuts work while this tab has focus and no text field is active.</span>
        <button
          type="button"
          className="ui-btn"
          disabled={Object.keys(stored).length === 0}
          onClick={() => {
            for (const id of Object.keys(stored)) attempt(studio, () => studio.commands.setHotkey(id, []));
            setMessage(null);
          }}
        >
          Reset all to defaults
        </button>
      </div>
      {message ? (
        <div className="ui-warn-text" role="alert" style={{ marginBottom: 8 }}>
          {message}
        </div>
      ) : null}
      {[...groups].map(([group, actions]) => (
        <section key={group} aria-label={`${group} shortcuts`}>
          <div className="ui-section-title">{group}</div>
          <table className="ui-table">
            <tbody>
              {actions.map((action) => {
                const chords = bindings[action.id] ?? [];
                return (
                  <tr key={action.id}>
                    <th scope="row" style={{ fontWeight: 400, width: '42%' }}>
                      {action.label}
                    </th>
                    <td style={{ whiteSpace: 'normal' }}>
                      {chords.map((chord) => {
                        const shared = conflicts[chord];
                        return (
                          <span
                            key={chord}
                            className={shared ? 'hotkey-chord conflict' : 'hotkey-chord'}
                            title={shared ? `Also bound to: ${shared.filter((o) => o !== action.id).map(labelOf).join(', ')}` : undefined}
                          >
                            {shared ? <Icon name="warning" size={12} title="Conflict" /> : null}
                            {formatChord(chord, mac)}
                            <button
                              type="button"
                              className="ui-icon-btn"
                              style={{ width: 18, height: 18 }}
                              aria-label={`Remove ${formatChord(chord)} from ${action.label}`}
                              onClick={() => save(action.id, chords.filter((c) => c !== chord))}
                            >
                              <Icon name="close" size={10} />
                            </button>
                          </span>
                        );
                      })}
                      {capturing === action.id ? (
                        <Capture onChord={(chord) => add(action.id, chord)} onCancel={() => setCapturing(null)} />
                      ) : (
                        <button type="button" className="ui-btn" style={{ minHeight: 22 }} aria-label={`Add shortcut for ${action.label}`} onClick={() => setCapturing(action.id)}>
                          Add
                        </button>
                      )}
                    </td>
                    <td style={{ width: 70, textAlign: 'right' }}>
                      <button
                        type="button"
                        className="ui-btn"
                        style={{ minHeight: 22 }}
                        disabled={stored[action.id] === undefined}
                        aria-label={`Reset ${action.label} to default`}
                        onClick={() => attempt(studio, () => studio.commands.setHotkey(action.id, []))}
                      >
                        Reset
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}
